<template>
    <div class="max-w-4xl mx-auto w-full space-y-6">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ isEdit ? "Edit Forum" : "Create Forum" }}
            </h1>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <p class="text-sm text-danger-700 dark:text-danger-300">
                {{ error }}
            </p>
        </div>

        <!-- Form -->
        <form
            @submit.prevent="handleSubmit"
            class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6"
        >
            <div v-if="!isEdit">
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Class Subject *
                </label>
                <SearchableSelect
                    v-model="form.class_subject_id"
                    :options="classSubjectOptions"
                    placeholder="Select Class Subject"
                    :option-label="getClassSubjectLabel"
                    option-value="id"
                    :required="true"
                />
            </div>

            <div v-if="!isEdit && !isTeacher">
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Teacher *
                </label>
                <SearchableSelect
                    v-model="form.teacher_id"
                    :options="teacherOptions"
                    placeholder="Select Teacher"
                    :option-label="getTeacherLabel"
                    option-value="id"
                />
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Title *
                </label>
                <input
                    v-model="form.title"
                    type="text"
                    placeholder="Enter forum title"
                    class="form-input w-full"
                />
            </div>

            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Description
                </label>
                <textarea
                    v-model="form.description"
                    rows="3"
                    placeholder="What is this forum about?"
                    class="form-input w-full"
                ></textarea>
            </div>

            <div class="space-y-3">
                <label class="flex items-center gap-3">
                    <input
                        v-model="form.allow_student_posts"
                        type="checkbox"
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span class="text-sm text-gray-700 dark:text-gray-300">
                        Allow student posts
                    </span>
                </label>

                <label class="flex items-center gap-3">
                    <input
                        v-model="form.require_approval"
                        type="checkbox"
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span class="text-sm text-gray-700 dark:text-gray-300">
                        Require approval for student posts (moderation)
                    </span>
                </label>

                <label class="flex items-center gap-3">
                    <input
                        v-model="form.is_active"
                        type="checkbox"
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span class="text-sm text-gray-700 dark:text-gray-300">
                        Active
                    </span>
                </label>
            </div>

            <div class="flex justify-end gap-3">
                <button
                    type="button"
                    @click="goBack"
                    class="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    :disabled="submitting"
                    class="btn btn-primary disabled:opacity-50"
                >
                    {{
                        submitting
                            ? "Saving..."
                            : isEdit
                              ? "Save Changes"
                              : "Create Forum"
                    }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import SearchableSelect from "@/components/SearchableSelect.vue";
import forumApi from "@/api/forum";
import { classSubjectApi } from "@/api/classSubject";
import { teacherApi } from "@/api/teacher";
import { useAuthStore } from "@/stores/authStore";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const forumId = route.params.id as string | undefined;
const isEdit = computed(() => Boolean(forumId));

const submitting = ref(false);
const error = ref<string | null>(null);

const classSubjectOptions = ref<any[]>([]);
const teacherOptions = ref<any[]>([]);

const isTeacher = computed(() => authStore.hasRole("teacher"));

const form = ref({
    class_subject_id: "",
    teacher_id: "",
    title: "",
    description: "",
    is_active: true,
    allow_student_posts: true,
    require_approval: false,
});

function goBack() {
    router.push("/forums");
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level || ""} ${option.class?.name || ""} - ${option.subject?.name || ""} (${option.semester?.name || "-"})`;
}

function getTeacherLabel(option: any) {
    return `${option.user?.name || "-"}${option.nip ? ` (${option.nip})` : ""}`;
}

async function fetchOptions() {
    const requests: Promise<any>[] = [
        classSubjectApi.getAll({ is_active: true, per_page: 1000 }),
    ];

    if (!isTeacher.value) {
        requests.push(teacherApi.getAll({ per_page: 1000 }));
    }

    const results = await Promise.allSettled(requests);

    if (results[0].status === "fulfilled") {
        classSubjectOptions.value = results[0].value?.data?.items || [];
    }

    if (results[1] && results[1].status === "fulfilled") {
        teacherOptions.value = (results[1].value as any)?.data?.items || [];
    }
}

async function fetchForum() {
    if (!forumId) return;

    try {
        const forum = await forumApi.getById(forumId);
        form.value = {
            class_subject_id: forum.class_subject_id,
            teacher_id: forum.teacher_id,
            title: forum.title,
            description: forum.description || "",
            is_active: forum.is_active,
            allow_student_posts: forum.allow_student_posts,
            require_approval: forum.require_approval,
        };
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to fetch forum";
    }
}

async function handleSubmit() {
    submitting.value = true;
    error.value = null;

    try {
        if (isEdit.value && forumId) {
            await forumApi.update(forumId, {
                title: form.value.title,
                description: form.value.description || undefined,
                is_active: form.value.is_active,
                allow_student_posts: form.value.allow_student_posts,
                require_approval: form.value.require_approval,
            });
        } else {
            const payload: any = {
                class_subject_id: form.value.class_subject_id,
                title: form.value.title,
                description: form.value.description || undefined,
                is_active: form.value.is_active,
                allow_student_posts: form.value.allow_student_posts,
                require_approval: form.value.require_approval,
            };

            if (!isTeacher.value && form.value.teacher_id) {
                payload.teacher_id = form.value.teacher_id;
            }

            await forumApi.create(payload);
        }

        router.push("/forums");
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to save forum";
    } finally {
        submitting.value = false;
    }
}

onMounted(() => {
    fetchOptions();
    fetchForum();
});
</script>
